// ให้ Qwen 0.5B "ปรับคำเล็กน้อย" ให้คำตอบที่เลือกจากไฟล์ไม่ซ้ำซากเกินไป — ต้องผ่านด่านตรวจทุกข้อ ไม่ผ่าน = ใช้ต้นฉบับ (ที่ตกแต่งแล้ว)
//  ไฟล์นี้ไม่ import engine โดยตรง (ฉีด generate เข้ามา) เพื่อทดสอบด่านตรวจ/prompt ได้โดยไม่ต้องมีโมเดล
import { stripParticles } from "./stripParticles";

export const GUARD = { minSim: 0.6, maxSim: 0.95, lenTol: 0.4 };
export const REWRITE_TEMPERATURE = 0.4; // ไม่ส่ง repetition_penalty (Qwen แยกภาษาไทยทีละตัวอักษร ค่าปรับซ้ำทำให้คำเพี้ยน)
export const REWRITE_TIMEOUT_MS = 8000;

export const REWRITE_SYSTEM =
  "You rewrite short Thai chat replies. Rewrite the sentence with slightly different words. Keep the same meaning, the same speaker and the same pronouns " +
  "(e.g. พี่ น้อง หนู ผม ข้า เจ้า เรา เธอ). Do not add new facts. Reply with ONLY the rewritten Thai sentence.";
export const REWRITE_EXAMPLE = { in: "ง่วงก็ไปนอนเถอะ อย่าฝืน", out: "ง่วงแล้วก็ไปนอนได้เลย ไม่ต้องฝืนนะ" };

/** messages สำหรับ engine.generate : ตัวอย่าง 1 คู่ + ประโยคจริง */
export const buildRewriteMessages = (text) => [
  { role: "user", content: REWRITE_EXAMPLE.in },
  { role: "assistant", content: REWRITE_EXAMPLE.out },
  { role: "user", content: String(text) },
];

const chars = (s) => Array.from(String(s));
/** ความเหมือนเชิงตัวอักษร 0..1 = 1 - (ระยะ Levenshtein / ความยาวที่ยาวกว่า) */
export function similarity(a, b) {
  const x = chars(a), y = chars(b);
  if (!x.length && !y.length) return 1;
  let prev = Array.from({ length: y.length + 1 }, (_, i) => i);
  for (let i = 1; i <= x.length; i++) {
    const cur = [i];
    for (let j = 1; j <= y.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1));
    prev = cur;
  }
  return 1 - prev[y.length] / Math.max(x.length, y.length);
}

const LATIN = /[A-Za-z]+/g;
const FOREIGN = /[぀-ヿ㐀-鿿가-힯Ѐ-ӿ؀-ۿ]/g; // ญี่ปุ่น/จีน/เกาหลี/ซีริลลิก/อาหรับ
/** ตัวอักษรละติน/ต่างประเทศที่ "ไม่มีในต้นฉบับ" */
export function foreignExtras(orig, cand) {
  const o = String(orig);
  const latin = new Set((o.match(LATIN) || []).map((w) => w.toLowerCase()));
  const extraLatin = (String(cand).match(LATIN) || []).filter((w) => !latin.has(w.toLowerCase()));
  const extraForeign = (String(cand).match(FOREIGN) || []).filter((c) => o.indexOf(c) === -1);
  return extraLatin.concat(extraForeign);
}

// สรรพนามต้องคงเดิม (บุคลิก/ผู้พูดห้ามเปลี่ยน) : จำนวนที่ปรากฏในผลต้องเท่าต้นฉบับ — โมเดล 0.5B ชอบตัดทิ้ง/สลับเป็น "คุณ" ; นับแบบ substring จึงเข้มไว้ก่อน (ปฏิเสธเกินดีกว่าปล่อยผิด)
export const PRONOUNS = ["ข้า", "เจ้า", "หนู", "ผม", "ฉัน", "เรา", "เธอ", "พี่", "น้อง", "คุณ", "ท่าน", "กู", "มึง"];
const countOf = (s, w) => String(s).split(w).length - 1;
export const pronounsChanged = (orig, cand) => PRONOUNS.some((w) => countOf(orig, w) !== countOf(cand, w));
// คำที่พลิกความหมาย/ชนิดประโยค (ปฏิเสธ + คำถาม) ต้องเหลือเท่าเดิม : "มาติวด้วยกันไหม" ห้ามกลายเป็นประโยคบอกเล่า, "ไม่โกรธ" ห้ามหาย "ไม่"
export const MEANING_WORDS = ["ไม่", "อย่า", "ห้าม", "ไหม", "มั้ย", "เหรอ", "หรือ", "อะไร", "ทำไม", "ไหน", "ยัง", "?", "？"];
export const meaningChanged = (orig, cand) => MEANING_WORDS.some((w) => countOf(orig, w) !== countOf(cand, w));

/** ด่านตรวจ -> {ok, reason, sim} */
export function checkRewrite(orig, cand) {
  const c = String(cand == null ? "" : cand).trim();
  if (!c) return { ok: false, reason: "empty", sim: 0 };
  if (/\{\{|\}\}/.test(c)) return { ok: false, reason: "placeholder", sim: 0 };
  const sim = similarity(orig, c);
  if (sim < GUARD.minSim) return { ok: false, reason: "too_different", sim };
  if (sim > GUARD.maxSim) return { ok: false, reason: "too_similar", sim };
  if (foreignExtras(orig, c).length) return { ok: false, reason: "foreign_chars", sim };
  if (pronounsChanged(orig, c)) return { ok: false, reason: "pronoun", sim };
  if (meaningChanged(orig, c)) return { ok: false, reason: "meaning_words", sim };
  const lo = chars(orig).length;
  const lc = chars(c).length;
  if (lc < lo * (1 - GUARD.lenTol) || lc > lo * (1 + GUARD.lenTol)) return { ok: false, reason: "length", sim };
  return { ok: true, reason: "ok", sim };
}

/** ทำความสะอาดผลดิบจากโมเดล: ตัดอัญประกาศครอบ/คำนำ/บรรทัดเกิน ; ตัดคำลงท้ายสุภาพเมื่อต้นฉบับไม่มี */
export function cleanCandidate(orig, raw) {
  let t = String(raw == null ? "" : raw).trim().split(/\r?\n/)[0].trim();
  t = t.replace(/^(?:rewritten|output|answer|คำตอบ|ประโยคใหม่)\s*[:：]\s*/i, "").replace(/^["'“”‘’「」『』*]+|["'“”‘’「」『』*]+$/g, "").trim();
  if (stripParticles(orig) === String(orig).trim()) t = stripParticles(t); // ต้นฉบับไม่ลงท้ายครับ/ค่ะ -> ผลก็ต้องไม่มี
  return t;
}

/**
 * ปรับคำ 1 ประโยค ; ไม่ throw ; ล้ม/ช้า/ไม่ผ่านด่าน = คืนต้นฉบับ
 * generate = engine.generate (ฉีดเข้ามา) ; onTimeout = ฟังก์ชันยกเลิกงานที่ค้าง (engine.abort)
 * คืน {text, rewritten, reason, sim, candidate}
 */
export async function rewriteReply(text, { generate, onTimeout, timeoutMs = REWRITE_TIMEOUT_MS } = {}) {
  const orig = String(text || "");
  if (!orig.trim() || typeof generate !== "function") return { text: orig, rewritten: false, reason: "skip", sim: 1, candidate: "" };
  let timer = null;
  try {
    const job = generate({
      system: REWRITE_SYSTEM, messages: buildRewriteMessages(orig), temperature: REWRITE_TEMPERATURE,
      maxTokens: Math.min(160, Math.ceil(chars(orig).length * 1.4) + 24),
    });
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => {
        if (onTimeout) onTimeout();
        resolve({ ok: false, code: "timeout" });
      }, timeoutMs);
    });
    const r = await Promise.race([job, timeout]);
    if (!r || !r.ok) return { text: orig, rewritten: false, reason: (r && r.code) || "error", sim: 1, candidate: "" };
    const cand = cleanCandidate(orig, r.text);
    const g = checkRewrite(orig, cand);
    if (!g.ok) return { text: orig, rewritten: false, reason: g.reason, sim: g.sim, candidate: cand };
    return { text: cand, rewritten: true, reason: "ok", sim: g.sim, candidate: cand };
  } catch (e) {
    return { text: orig, rewritten: false, reason: "error", sim: 1, candidate: "" };
  } finally {
    if (timer) clearTimeout(timer);
  }
}
